//logica para solicitud de vacaciones 
import { ConfirmationModal } from '../UI/ConfirmationModal'
import { ActionWithReasonModal } from '../UI/ActionWithReasonModal'
import { 
    doc, updateDoc, Timestamp,
    arrayUnion, runTransaction 
} from 'firebase/firestore';
import { db } from '../../firebase/config'
import { toast } from 'react-toastify';
import { useState } from 'react';

const getBadgeProps = (tipo) => {
    switch (tipo) {
        case 'vacaciones': return { label: 'Vacaciones', color: 'bg-blue-100 text-blue-800' };
        case 'llegada_tarde': return { label: 'Llegada Tarde', color: 'bg-yellow-100 text-yellow-800' };
        case 'salida_anticipada': return { label: 'Salida Anticipada', color: 'bg-orange-100 text-orange-800' };
        case 'falta_justificada': return { label: 'Falta Justificada', color: 'bg-purple-100 text-purple-800' };
        case 'home_office': return { label: 'Home Office', color: 'bg-teal-100 text-teal-800' };
        default: return { label: tipo || 'Otro', color: 'bg-gray-100 text-gray-800' };
    }
};

export const VacationRequestsTable = ({ requests, viewerRole, onActionComplete }) => {
    const [confirmingAction, setConfirmingAction] = useState(null);
    const [processingId, setProcessingId] = useState(null);

    const promptAction = (request, actionType) => {
        if (actionType === 'approve') {
            setConfirmingAction({
                type: 'approve',
                request,
                title: "Aprobar Solicitud",
                message: "¿Deseas aprobar esta solicitud?"
            });
        } else if (actionType === 'reject') {
            setConfirmingAction({
                type: 'reject',
                request,
                title: "Rechazar Solicitud",
                message: "Indica el motivo del rechazo:",
                confirmText: "Rechazar",
                confirmColor: "bg-red-600"
            });
        }
    };

    const handleApprove = async () => {
        if (!confirmingAction) return;
        const { request } = confirmingAction;
        setProcessingId(request.id);
        
        try {
            const requestRef = doc(db, "solicitudesVacaciones", request.id);

            if (viewerRole === 'supervisor') {
                await updateDoc(requestRef, { 
                    estado: 'Pendiente RH', 
                    aprobadoSupervisor: true,
                    fechaAprobacionSupervisor: Timestamp.now()
                });
                toast.success("Aprobado. Enviado a RH.");
            } 
            else if (viewerRole === 'rh') {
                if (request.tipo === 'vacaciones') {
                    await runTransaction(db, async (transaction) => {
                        const userRef = doc(db, "usuarios", request.uid);
                        
                        const nuevoRegistro = {
                            id: request.id,
                            fechaInicio: request.fechaInicio,
                            fechaFin: request.fechaFin,
                            dias: request.diasSolicitados,
                            motivo: request.motivo || "Solicitud App",
                            tipo: "vacaciones",
                            fechaRegistro: Timestamp.now()
                        };
                        
                        transaction.update(userRef, { historialVacaciones: arrayUnion(nuevoRegistro) });
                        
                        transaction.update(requestRef, { 
                            estado: 'Aprobado', 
                            aprobadoRH: true,
                            fechaAprobacionRH: Timestamp.now()
                        });
                    });
                    toast.success("Vacaciones aprobadas y días descontados.");
                } else {
                    await updateDoc(requestRef, { 
                        estado: 'Aprobado', 
                        aprobadoRH: true,
                        fechaAprobacionRH: Timestamp.now()
                    });
                    toast.success(`Solicitud de ${request.tipo} aprobada.`);
                }
            }
            onActionComplete();
        } catch (error) {
            console.error(error);
            toast.error("Error al procesar.");
        } finally {
            setProcessingId(null);
            setConfirmingAction(null);
        }
    };

    const handleReject = async (reason) => {
        if (!confirmingAction) return;
        const { request } = confirmingAction;
        
        if (!reason || reason.trim() === "") {
            alert("Debes escribir un motivo.");
            return;
        }

        setProcessingId(request.id);
        try {
            const requestRef = doc(db, "solicitudes", request.id);
            await updateDoc(requestRef, { 
                estado: 'Rechazado', 
                rechazadoPor: viewerRole,
                motivoRechazo: reason,
                fechaRechazo: Timestamp.now()
            });
            toast.info("Solicitud rechazada.");
            onActionComplete();
        } catch (error) {
            console.error(error);
            toast.error("Error al rechazar.");
        } finally {
            setProcessingId(null);
            setConfirmingAction(null);
        }
    };

    return (
        <>
            <div className="overflow-x-auto bg-white rounded-lg shadow">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Empleado</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th> 
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fechas</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Info</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {requests.length === 0 ? (
                            <tr><td colSpan="5" className="px-6 py-4 text-center text-gray-500">No hay solicitudes pendientes.</td></tr>
                        ) : (
                            requests.map(req => {
                                const badge = getBadgeProps(req.tipo);
                                
                                return (
                                <tr key={req.id}>
                                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                                        {req.nombre} <br/>
                                        <span className="text-xs text-gray-500 capitalize">{req.rol}</span>
                                    </td>
                                    
                                    <td className="px-6 py-4">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${badge.color}`}>
                                            {badge.label}
                                        </span>
                                    </td>

                                    <td className="px-6 py-4 text-sm text-gray-600">
                                        {req.fechaInicio?.toDate().toLocaleDateString()} 
                                        {req.tipo === 'vacaciones' && ` - ${req.fechaFin?.toDate().toLocaleDateString()}`}
                                    </td>
                                    
                                    <td className="px-6 py-4 text-sm text-gray-600">
                                        {req.tipo === 'vacaciones' ? (
                                             <span className="font-bold text-blue-600">{req.diasSolicitados} días</span>
                                        ) : (
                                            <span className="italic text-gray-500">{req.motivo || 'Sin motivo'}</span>
                                        )}
                                    </td>
                                    
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                                        <button 
                                            onClick={() => promptAction(req, 'approve')} 
                                            disabled={processingId === req.id}
                                            className="text-green-600 hover:text-green-900 font-bold disabled:opacity-50"
                                        >
                                            Aprobar
                                        </button>
                                        <button 
                                            onClick={() => promptAction(req, 'reject')} 
                                            disabled={processingId === req.id}
                                            className="text-red-600 hover:text-red-900 font-bold disabled:opacity-50"
                                        >
                                            Rechazar
                                        </button>
                                    </td>
                                </tr>
                            )})
                        )}
                    </tbody>
                </table>
            </div>

            {confirmingAction?.type === 'approve' && (
                <ConfirmationModal 
                    title={confirmingAction.title} 
                    message={confirmingAction.message} 
                    onConfirm={handleApprove} 
                    onCancel={() => setConfirmingAction(null)} 
                />
            )}
            {confirmingAction?.type === 'reject' && (
                <ActionWithReasonModal 
                    title={confirmingAction.title} 
                    message={confirmingAction.message} 
                    onConfirm={handleReject} 
                    onCancel={() => setConfirmingAction(null)} 
                    confirmText={confirmingAction.confirmText}
                    confirmColor={confirmingAction.confirmColor}
                />
            )}
        </>
    );
};